import axios from 'axios';
import { feathers } from '@/feathers-client';
import { ESSearchHeader } from '@/Core/config/es-search-header';

export const RELATION_TYPE_SPOUSE = 'Spouse';

const initialState = {
  relatedContacts: [],
  originalRelatedContacts: [],
  relatedContactsIds: [],
  relatedContactsError: null,
  relationshipTypes: [],
  searchResults: [],
  showSearchMenu: false,
  error: null,
  responseSaveRelationship: null,
  saveRelationshipError: null,
  isDeleteSuccess: false,
  isDeleteError: false,
  deleteErrorMessage: '',
  showModal: false,
  relationshipsTypeChanges: [],
  relationshipsErrorsResponse: [],
  allRelsSuccessful: false,
  relsErrors: false,
};

export default {
  namespaced: true,
  state: { ...initialState },
  getters: {
    relsErrorsObj(state) {
      return state.relationshipsErrorsResponse
        .reduce((acc, cur) => ({...acc, [cur.assigned_contact_id]: cur.message}), {});
    },
    relationshipsCount(state) {
      return state.relatedContactsIds.length;
    },
    relationshipsSpouse(state) {
      return state.relatedContacts.filter(p => p.relationship.name === RELATION_TYPE_SPOUSE);
    },
    relationshipTypes(state) {
      return state.relationshipTypes.map(type => {
        return { textCustomerType: type.name, idCustomerType: type.id, disabled: false };
      });
    },
    relationshipTypeItems(state) {
      return state.relationshipTypes.map(type => {
        return { text: type.name, value: type.id, disabled: false };
      });
    },
    relationshipTypesWithName(state) {
      return state.relationshipTypes
        .map(type => {
          return { name: type.name, id: type.id, disabled: false };
        });
    },
  },
  mutations: {
    setRelatedContacts(state, payload) {
      state.relatedContacts = payload;
    },
    setOriginalRelatedContacts(state, payload) {
      state.originalRelatedContacts = JSON.parse(JSON.stringify(payload));
    },
    setRelationshipTypes(state, payload) {
      state.relationshipTypes = payload;
    },
    setRelatedContactsError(state, err) {
      state.relatedContactsError = err;
    },
    setSearchResults(state, payload) {
      state.searchResults = payload;
    },
    setContactSearchError(state, err) {
      state.error = err;
    },
    setShowSearchMenu(state, payload) {
      state.showSearchMenu = payload;
    },
    setSaveRelationshipResponse(state, payload) {
      state.responseSaveRelationship = payload;
    },
    setSaveRelationshipError(state, payload) {
      state.saveRelationshipError = payload;
    },
    setRelationshipErrorsResponse(state, payload) {
      state.relationshipsErrorsResponse = [...payload];
    },
    setMappedIDsOfRelatedContacts(state, payload) {
      state.relatedContactsIds = payload;
    },
    setUnselectSingleContact(state, payload) {
      const { searchedContacts, contact } = payload;
      const unselectContact = searchedContacts.map(c => {
        if (c.id === contact.id) {
          c.isSelected = false;
        }
        return c;
      });
      state.searchResults = [...unselectContact];
    },
    setUnselectAllContacts(state, payload) {
      const unselect = payload.map(c => {
        c.isSelected = false;
        return c;
      });
      state.searchResults = [...unselect];
    },
    setDeleteSuccess(state) {
      state.isDeleteSuccess = true;
    },
    setDeleteError(state, payload) {
      state.isDeleteError = true;
      state.deleteErrorMessage = payload;
    },
    clearDeleteMessages(state) {
      state.isDeleteSuccess = false;
      state.isDeleteError = false;
      state.deleteErrorMessage = '';
    },
    setShowModal(state, show) {
      state.showModal = show;
    },
    resetResponseSaveRelationship(state) {
      state.responseSaveRelationship = null;
    },
    resetRelationshipsErrorsResponse(state) {
      state.relationshipsErrorsResponse = [];
    },
    setRelationshipsTypeChanges(state, payload) {
      const index = state.relationshipsTypeChanges.findIndex(
        item => item.relationship_id === payload.relationship_id
      );

      if (index > -1) {
        state.relationshipsTypeChanges[index].relationship_type_id = payload.relationship_type_id;
        return;
      }

      state.relationshipsTypeChanges.push(payload);
    },
    resetRelationshipsTypeChanges(state) {
      state.relationshipsTypeChanges = [];
    },
    setAllRelsSuccessful(state, bool) {
      state.allRelsSuccessful = bool;
    },
    setRelsErrors(state, bool) {
      state.relsErrors = bool;
    }
  },

  actions: {
    async searchContacts({ commit }, payload) {
      const { search, selectedContacts, currentUserId, contactId } = payload;

      if (!!search && search.length > 1) {
        const query = {
          $and: [{ $or: [{ owner_user_id: currentUserId }] }],
          $keywords: search,
          $limit: 100,
          $teams: false,
        };

        let response;
        try {
          response = await feathers
            .service('cached-contacts/search')
            .create({ query }, { headers: ESSearchHeader });
        } catch (error) {
          commit('setContactSearchError', true);
          commit('setShowSearchMenu', false);
        }
        const data = [...response.data];
        const mappedContacts = data.map(val => {
          const contact = {
            id: val.id,
            first_name: val.first_name,
            last_name: val.last_name,
          };

          const findIfSelected = selectedContacts.find(c => c.id === val.id);
          if (findIfSelected && !val.isSelected) {
            contact.isSelected = true;
          }

          return contact;
        });
        const searchResults = mappedContacts.filter(val => val.id !== contactId);
        commit('setSearchResults', searchResults);
        commit('setShowSearchMenu', true);
      } else {
        commit('setShowSearchMenu', false);
      }
    },

    async fetchContactRelationships({ commit }, contactId) {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/contacts/${contactId}/relationships`,
        );
        const mappedIDs = res.data.related_contacts.map(c => c.related_contact.id);
        commit('setRelatedContacts', res.data.related_contacts);
        commit('setOriginalRelatedContacts', res.data.related_contacts);
        commit('setMappedIDsOfRelatedContacts', mappedIDs);
      } catch (error) {
        commit('setRelatedContactsError', error);
      }
    },

    async fetchRelationshipTypes({ commit, rootState }) {
      try {
        const types = await feathers.service('contact-types').find({
          query: {
            $paginate: false,
            $sort: { name: 1 },
          },
        });
        commit('setRelationshipTypes', types);
      } catch (error) {
        commit('setRelatedContactsError', error);
      }
    },

    async createContactRelationships({ commit }, { contactId, relationships }) {
      try {
        const payload = {
          main_contact_id: contactId,
          relationships,
        };
        const res = await axios.post(
          `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/relationships`,
          payload,
        );
        if (!res.data.errors) {
          commit('setAllRelsSuccessful', true);
        } else {
          commit('setRelsErrors', true);
          commit('setRelationshipErrorsResponse', res.data.errors);
        }
        commit('setSaveRelationshipResponse', res.data.success);
      } catch (error) {
        commit('setSaveRelationshipError', error);
        throw error;
      }
    },

    async deleteContactRelationship({ commit }, { contactId, relationshipId }) {
      try {
        await axios.delete(
          `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/contacts/${contactId}/relationships/${relationshipId}`,
        );
        commit('setDeleteSuccess');
      } catch (error) {
        commit('setDeleteError', error)
      }
    },

    setShowModal({ commit }, show) {
      commit('setShowModal', show);
    },

    setShowSearchMenu({ commit }, show) {
      commit('setShowSearchMenu', show);
    },

    setUnselectSingleContact({ commit }, payload) {
      commit('setUnselectSingleContact', payload);
    },

    setUnselectAllContacts({ commit }, payload) {
      commit('setUnselectAllContacts', payload);
    },

    resetResponseSaveRelationship({ commit }) {
      commit('resetResponseSaveRelationship');
    },
    setAllRelsSuccessful({ commit }, bool) {
      commit('setAllRelsSuccessful', bool);
    },
    setRelsErrors({ commit }, bool) {
      commit('setRelsErrors', bool);
    },
    setRelationshipsTypeChanges({ commit }, relationshipContactType) {
      commit('setRelationshipsTypeChanges', relationshipContactType);
    },
    resetRelationships({ commit, state }) {
      const originalData = JSON.parse(JSON.stringify(state.originalRelatedContacts));
      commit('setRelatedContacts', originalData);
      commit('resetRelationshipsTypeChanges');
    },
    resetRelationshipsTypeChanges({ commit }) {
      commit('resetRelationshipsTypeChanges');
    },
  },
};
